import * as React from 'react';

import { cn } from '../utils/cn';
import {
  segmentedControlItemActiveClass,
  segmentedControlItemClass,
  segmentedControlTrackClass,
} from '../utils/design-surface-styles';

export type SegmentedControlOption<T extends string> = {
  value: T;
  label: React.ReactNode;
};

export type SegmentedControlProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  ariaLabel: string;
  /** Stretch segments to fill the track (mobile toolbars). */
  fullWidth?: boolean;
  className?: string;
  /** Extra classes per segment (height / padding overrides). */
  itemClassName?: string;
};

/** Token-driven segmented switch (`--segmented-*`) for ranges and view modes. */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  fullWidth = false,
  className,
  itemClassName,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        segmentedControlTrackClass,
        fullWidth && 'w-full',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              segmentedControlItemClass,
              'inline-flex h-[34px] min-w-0 items-center justify-center gap-1 px-3 text-[13px] whitespace-nowrap',
              fullWidth && 'flex-1 basis-0',
              active && segmentedControlItemActiveClass,
              itemClassName,
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
